/**
 * Academy domain — `AcademyLivesStore` state shape. Remote-only, in-memory.
 *
 * Acervo (public recording view models) and OData read entities are kept in
 * SEPARATE maps on purpose: `LiveEventEntity` carries private ingest/recording
 * fields (`CloudflareInputId`, `CloudflareVideoUid`, …) that must never leak into
 * the public `acervo()` collection. Merging both into one map would let a later
 * acervo read inherit private fields from an earlier entity read for the same id.
 */
import type { Guid } from './academy-enums.model';
import type {
  LiveEventMetricsResponse,
  LiveEventResponse,
} from './academy.model';
import type { LiveEventEntity } from './academy-entities.model';
import type {
  LiveAttachmentResponse,
  MyLiveSubscriptionSummary,
} from './academy-responses.model';

export type LiveEventRecord = LiveEventResponse | LiveEventEntity;

export interface AcademyLivesState {
  /** Public acervo / recording view models by id — never carries private OData fields. */
  acervoById: Record<Guid, LiveEventResponse>;
  /** OData read entities by id (carry private ingest/recording fields). Kept apart from `acervoById`. */
  entityById: Record<Guid, LiveEventEntity>;
  /** Order of the acervo result. */
  acervoIds: Guid[];
  /** Order of the last generic `list` result. */
  listIds: Guid[];
  /** Currently focused event. */
  selectedId: Guid | null;
  /**
   * Event created by the current or last `assembleLiveEvent`, set the moment
   * `POST /LiveEvents` lands (before the cover and support materials). With
   * `assembleLiveEventError` it means the live EXISTS but its assembly did not finish,
   * so the builder must open it for editing instead of POSTing a duplicate. Cleared when
   * an assembly starts and by `resetAssemble`.
   */
  assembledEventId: Guid | null;
  /** Per-event aggregated metrics (producer). */
  metricsByEvent: Record<Guid, LiveEventMetricsResponse>;
  /** Playback tokens for live recordings, keyed `event:{id}`. (Course-item tokens live in `AcademyLearningStore`.) */
  playbackTokenByKey: Record<string, string>;
  /** Support-material descriptors per event, from `GET /LiveEvents/{id}/attachments`. */
  attachmentsByEvent: Record<Guid, LiveAttachmentResponse[]>;
  /**
   * Events the current viewer has subscribed to (reminder bell) this session.
   * Session-local and optimistic: used for immediate UI feedback on toggle.
   * Server truth is available via `mySubscriptions` (loaded from `GET /LiveSubscriptions/mine`).
   */
  subscribedEventIds: Guid[];
  /** Student's saved lives from `GET /LiveSubscriptions/mine` (server truth). */
  mySubscriptions: MyLiveSubscriptionSummary[];
  /** Server-reported total for `GET /LiveSubscriptions/mine` (for pagination). */
  mySubscriptionsTotal: number;
}

export const initialAcademyLivesState: AcademyLivesState = {
  acervoById: {},
  entityById: {},
  acervoIds: [],
  listIds: [],
  selectedId: null,
  assembledEventId: null,
  metricsByEvent: {},
  playbackTokenByKey: {},
  attachmentsByEvent: {},
  subscribedEventIds: [],
  mySubscriptions: [],
  mySubscriptionsTotal: 0,
};
